// utils/tree-dnd.ts — Glisser-déposer d'un arbre `struct-*`, partagé.
//
// `page-admin-structures.ts` (arbre des organisations) et
// `page-action-plans.ts` (graphe des prédécesseurs de volets) portaient chacun
// leur copie des mêmes gestionnaires : `onTreeDragStart` était BYTE POUR BYTE
// identique, `onTreeDragOver` à 94 %, `onTreeDrop` à 90 %, plus deux helpers
// DOM strictement identiques (audit front du 2026-09-10).
//
// La logique PURE du graphe était déjà partagée (`domain/structure-graph.ts`,
// `domain/action-plan.ts`) : seule la glu DOM ne l'était pas — et c'est elle
// qui n'avait aucun test, la couverture bloquante ne portant que sur
// `domain/`. D'où ce module : les trois gestionnaires deviennent une fabrique
// paramétrée par ce qui diffère réellement entre les deux pages, ce qui les
// rend testables sans monter une page entière.
//
// Ce que les deux pages ont en commun et qui reste figé ici : les classes CSS
// `struct-row`, `struct-dragging`, `struct-drop-target`, `struct-drop-invalid`
// et l'usage de `Shift` pour « ajouter le lien » au lieu de « déplacer ».

import type { DragState, DropStatus } from '../domain/action-plan.js';

/** Ligne d'arbre portant l'événement, ou `null` hors d'une ligne. */
export function rowFromEvent(e: Event): HTMLElement | null {
  const el = (e.target as HTMLElement | null)?.closest('.struct-row');
  return el instanceof HTMLElement ? el : null;
}

/** Retire tous les surlignages de dépôt laissés par un survol précédent. */
export function clearDropHints(root: ParentNode = document): void {
  root
    .querySelectorAll('.struct-drop-target,.struct-drop-invalid,.struct-rootzone.dnd-over')
    .forEach((el) => {
      el.classList.remove('struct-drop-target', 'struct-drop-invalid', 'dnd-over');
    });
}

/** Id de nœud porté par une ligne, ou `null` si absent/illisible. */
export function nodeIdOf(row: HTMLElement, attr: 'node' | 'parent' = 'node'): number | null {
  const raw = row.dataset[attr];
  if (raw == null || raw === '') return null;
  const n = parseInt(raw, 10);
  return Number.isNaN(n) ? null : n;
}

export interface TreeDndDeps {
  /** Lit l'état de drag courant (détenu par la page). */
  getDrag: () => DragState | null;
  /** Pose l'état de drag (ou `null` en fin de geste). */
  setDrag: (d: DragState | null) => void;
  /** Descendants du nœud déplacé — sert à refuser les cycles. */
  descendantIds: (rootId: number) => Set<number>;
  /** Statut d'une cible : `valid` / `noop` / `invalid`. */
  targetStatus: (targetId: number) => DropStatus;
  /** Applique le relink. `add` vrai = ajouter le lien (Shift) au lieu de déplacer. */
  applyRelink: (child: number, fromParent: number | null, target: number, add: boolean) => void;
  /** Racine de recherche DOM — le document par défaut, un fragment en test. */
  root?: ParentNode;
}

export interface TreeDndHandlers {
  onDragStart: (e: DragEvent) => void;
  onDragOver: (e: DragEvent) => void;
  onDrop: (e: DragEvent) => void;
}

/**
 * Fabrique les trois gestionnaires d'un arbre `struct-*`.
 *
 * Tout ce qui diffère entre les deux pages passe par `deps` : la détention de
 * l'état de drag, la source du graphe, et la forme de l'appel de relink —
 * `page-action-plans` prend un booléen positionnel, `page-admin-structures`
 * un objet `{ add }`. L'adaptation se fait chez l'appelant, pas ici.
 */
export function createTreeDndHandlers(deps: TreeDndDeps): TreeDndHandlers {
  const root = deps.root ?? document;

  function onDragStart(e: DragEvent): void {
    const row = rowFromEvent(e);
    if (!row) return;
    const child = nodeIdOf(row);
    if (child == null) return;
    deps.setDrag({ child, fromParent: nodeIdOf(row, 'parent') });
    if (e.dataTransfer) {
      // copyMove : Shift bascule le curseur en « copier » (ajout de lien).
      e.dataTransfer.effectAllowed = 'copyMove';
      e.dataTransfer.setData('text/plain', String(child));
    }
    // Grise tout le sous-arbre déplacé — le nœud ET ses descendants, à toutes
    // leurs occurrences dans l'arbre — pour montrer que le sous-arbre suit.
    const moving = deps.descendantIds(child);
    moving.add(child);
    root.querySelectorAll<HTMLElement>('.struct-row').forEach((r) => {
      const n = nodeIdOf(r);
      if (n != null && moving.has(n)) r.classList.add('struct-dragging');
    });
  }

  function onDragOver(e: DragEvent): void {
    if (!deps.getDrag()) return;
    const row = rowFromEvent(e);
    if (!row) return;
    const targetId = nodeIdOf(row);
    if (targetId == null) return;
    const status = deps.targetStatus(targetId);
    clearDropHints(root);
    if (status === 'invalid') {
      // Impossible (boucle / soi-même) → surlignage orange, drop refusé.
      row.classList.add('struct-drop-invalid');
      return;
    }
    if (status === 'noop') return;
    e.preventDefault(); // autorise le drop
    if (e.dataTransfer) e.dataTransfer.dropEffect = e.shiftKey ? 'copy' : 'move';
    row.classList.add('struct-drop-target');
  }

  function onDrop(e: DragEvent): void {
    const drag = deps.getDrag();
    if (!drag) return;
    const row = rowFromEvent(e);
    if (!row) return;
    const targetId = nodeIdOf(row);
    if (targetId == null || deps.targetStatus(targetId) !== 'valid') return;
    e.preventDefault();
    // Shift = ajouter le lien (duplication) au lieu de déplacer.
    deps.applyRelink(drag.child, drag.fromParent, targetId, e.shiftKey);
  }

  return { onDragStart, onDragOver, onDrop };
}
